import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  EyeIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [extendModal, setExtendModal] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [actionLoading, setActionLoading] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addGameModal, setAddGameModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, filter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        filter,
        ...(search && { search }),
      });
      const response = await apiClient.get(`/api/auth/admin/subscriptions/?${params}`);
      setSubscriptions(response.subscriptions || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSubscriptions();
  };

  const handleExtend = async (id) => {
    try {
      setActionLoading(id);
      await apiClient.post(`/api/auth/admin/subscriptions/${id}/extend/`, { days: extendDays });
      setExtendModal(null);
      setExtendDays(30);
      fetchSubscriptions();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to extend subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this subscription? This will expire it immediately.')) return;
    try {
      setActionLoading(id);
      await apiClient.delete(`/api/auth/admin/subscriptions/${id}/revoke/`);
      fetchSubscriptions();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to revoke subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const fetchSubscriptionDetail = async (id) => {
    try {
      setDetailLoading(true);
      const data = await apiClient.get(`/api/auth/admin/subscriptions/${id}/`);
      setDetailModal(data);
    } catch (error) {
      alert('Failed to fetch subscription details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAddGameAccess = async () => {
    if (!selectedGame || !detailModal) return;
    try {
      await apiClient.post(`/api/auth/admin/subscriptions/${detailModal.id}/game-access/`, {
        game_type: selectedGame
      });
      setAddGameModal(false);
      setSelectedGame('');
      fetchSubscriptionDetail(detailModal.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to add game access');
    }
  };

  const handleRemoveGameAccess = async (gameAccessId) => {
    if (!window.confirm('Remove this game access?')) return;
    try {
      await apiClient.delete(`/api/auth/admin/game-access/${gameAccessId}/`);
      fetchSubscriptionDetail(detailModal.id);
    } catch (error) {
      alert('Failed to remove game access');
    }
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <form onSubmit={handleSearch} className="flex gap-3 mb-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user or course..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
            Search
          </button>
        </form>
        <div className="flex gap-2">
          {[
            { value: 'active', label: 'Active' },
            { value: 'expired', label: 'Expired' },
            { value: 'all', label: 'All' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setCurrentPage(1); }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === f.value
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Course / Tier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Purchased</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Days Left</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                subscriptions.map(sub => (
                  <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{sub.user_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{sub.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">{sub.course_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{sub.tier_name}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {sub.amount_paid.toFixed(2)} JOD
                    </td>
                    <td className="px-4 py-3">
                      {sub.is_active ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                          <CheckCircleIcon className="h-3.5 w-3.5" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-medium text-red-500 dark:text-red-400">
                          <XCircleIcon className="h-3.5 w-3.5" /> Expired
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                      {new Date(sub.purchased_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                      {sub.access_expires_at ? new Date(sub.access_expires_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${
                        sub.days_remaining > 30 ? 'text-green-600 dark:text-green-400' :
                        sub.days_remaining > 7 ? 'text-amber-600 dark:text-amber-400' :
                        sub.days_remaining > 0 ? 'text-red-600 dark:text-red-400' :
                        'text-gray-400'
                      }`}>
                        {sub.days_remaining > 0 ? `${sub.days_remaining}d` : 'Expired'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => fetchSubscriptionDetail(sub.id)}
                          disabled={actionLoading === sub.id}
                          className="p-1.5 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                          title="View details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setExtendModal(sub)}
                          disabled={actionLoading === sub.id}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg disabled:opacity-50"
                          title="Extend subscription"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        {sub.is_active && (
                          <button
                            onClick={() => handleRevoke(sub.id)}
                            disabled={actionLoading === sub.id}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50"
                            title="Revoke subscription"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} subscriptions)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={!pagination.has_previous}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!pagination.has_next}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Extend Modal */}
      {extendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Extend Subscription</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Extend subscription for <strong>{extendModal.user_name}</strong> ({extendModal.course_name})
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Days to extend
              </label>
              <input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setExtendModal(null); setExtendDays(30); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExtend(extendModal.id)}
                disabled={extendDays < 1 || actionLoading === extendModal.id}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading === extendModal.id ? 'Extending...' : `Extend by ${extendDays} days`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Subscription Details</h3>
              <button onClick={() => setDetailModal(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {detailLoading ? (
              <div className="py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* User Info */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">User Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Name:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{detailModal.user?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Email:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{detailModal.user?.email}</span>
                    </div>
                  </div>
                </div>

                {/* Subscription Info */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Subscription Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Course:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{detailModal.course?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Tier:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{detailModal.tier_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Amount:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{detailModal.amount_paid?.toFixed(2)} {detailModal.currency}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Payment Method:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium capitalize">{detailModal.payment_method}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <span className={`ml-2 font-medium ${detailModal.is_active ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                        {detailModal.is_active ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Days Remaining:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{detailModal.days_remaining}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Purchased:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {new Date(detailModal.purchased_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Expires:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {detailModal.access_expires_at ? new Date(detailModal.access_expires_at).toLocaleDateString() : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Game Access */}
                {detailModal.course?.code === 'DLR' && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Game Access</h4>
                      <button
                        onClick={() => setAddGameModal(true)}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <PlusIcon className="h-3 w-3" /> Add Game
                      </button>
                    </div>
                    {detailModal.game_accesses?.length > 0 ? (
                      <div className="space-y-2">
                        {detailModal.game_accesses.map(ga => (
                          <div key={ga.id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded p-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{ga.game_type}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                ga.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {ga.is_active ? 'Active' : 'Expired'}
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveGameAccess(ga.id)}
                              className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No game access added</p>
                    )}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={() => setDetailModal(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Game Access Modal */}
      {addGameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Game Access</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Game
              </label>
              <select
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Choose a game...</option>
                {['SKT', 'MIC', 'MEK', 'RMS', 'ROT', 'OWT', 'PPT'].map(game => (
                  <option key={game} value={game}>{game}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setAddGameModal(false); setSelectedGame(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAddGameAccess}
                disabled={!selectedGame}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Add Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptions;
